// The Gmail add-on's web wire contract: what `/api/gmail/link` and
// `/api/gmail/proposals` send and the web parses.
//
// Declared once here, for both ends: zod strips an undeclared key, so a field
// the server adds without adding it here never reaches the page.

import { z } from "zod";

import { MEETING_ISSUE_STATUSES } from "../meetings/dto.js";

const isoDate = z.string().min(1);

/**
 * `GET /api/gmail/link`: always answers, flag on or off. `linked` and
 * `linkedAt` are absent while the feature is off. The Google account's
 * e-mail is never part of it: the link holds none.
 */
export const GmailLinkStatusSchema = z.object({
  enabled: z.boolean(),
  linked: z.boolean().optional(),
  /** When the current link was made; null when not linked. */
  linkedAt: isoDate.nullable().optional(),
});
export type GmailLinkStatus = z.infer<typeof GmailLinkStatusSchema>;

/**
 * How a filed mail proposal stands now, read live from the issue's labels and
 * state: the meetings vocabulary, because Accept and Decline are the
 * meetings review helpers.
 */
export const MAIL_PROPOSAL_STATUSES = MEETING_ISSUE_STATUSES;
export type MailProposalStatus = (typeof MAIL_PROPOSAL_STATUSES)[number];

/**
 * One of the caller's filed mail proposals for a project ("Vorschläge aus
 * Mails"): one topic of a mail, one issue (a mail of several topics is
 * several rows). `topicId` is what Accept and Decline name; `draftId` is the
 * mail's. `title` and `status` come from a live forge read and are null when
 * it failed.
 */
export const MailProposalRowDto = z.object({
  topicId: z.string(),
  draftId: z.string(),
  projectId: z.number().int(),
  issueIid: z.number().int().positive(),
  title: z.string().nullable(),
  status: z.enum(MAIL_PROPOSAL_STATUSES).nullable(),
  createdAt: isoDate,
});
export type MailProposalRowDto = z.infer<typeof MailProposalRowDto>;

/** `GET /api/gmail/proposals?projectId=`: the caller's proposals still open (live status `new`), newest first, at most 20. */
export const MailProposalListDto = z.object({
  proposals: z.array(MailProposalRowDto),
});
export type MailProposalListDto = z.infer<typeof MailProposalListDto>;
